import React, { useState, useEffect, useMemo } from 'react';
import { Zap, Plus, Trash2, Edit, Clock, Activity, Search, Folder, FolderPlus, X, ChevronRight, ChevronDown, MoreVertical, Settings, Pencil, RotateCcw } from 'lucide-react';
import api from '../utils/api';
import WorkflowBuilder from './WorkflowBuilder';
import WorkflowExecutionLogs from './WorkflowExecutionLogs';

const AutomationsSettings = ({ user }) => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [view, setView] = useState('list');
  
  // Search and folder state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null); // null = All, '' = Uncategorized
  const [expandedFolders, setExpandedFolders] = useState({});
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [workflowToMove, setWorkflowToMove] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const response = await api.get('/workflow-automations');
      setWorkflows(response.data.workflows || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching workflows:', err);
      setError(err.response?.data?.detail || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  // Get unique folders from workflows
  const folders = useMemo(() => {
    const folderSet = new Set();
    workflows.forEach(wf => {
      if (wf.folder) folderSet.add(wf.folder);
    });
    return Array.from(folderSet).sort();
  }, [workflows]);

  // Filter workflows by search and folder
  const filteredWorkflows = useMemo(() => {
    return workflows.filter(wf => {
      // Search filter
      const matchesSearch = !searchQuery || 
        wf.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (wf.description && wf.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (wf.folder && wf.folder.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Folder filter
      const matchesFolder = selectedFolder === null || // All
        (selectedFolder === '' && !wf.folder) || // Uncategorized
        wf.folder === selectedFolder;
      
      return matchesSearch && matchesFolder;
    });
  }, [workflows, searchQuery, selectedFolder]);

  // Group workflows by folder for display
  const workflowsByFolder = useMemo(() => {
    const grouped = { uncategorized: [] };
    folders.forEach(f => grouped[f] = []);
    
    filteredWorkflows.forEach(wf => {
      if (wf.folder) {
        if (!grouped[wf.folder]) grouped[wf.folder] = [];
        grouped[wf.folder].push(wf);
      } else {
        grouped.uncategorized.push(wf);
      }
    });
    
    return grouped;
  }, [filteredWorkflows, folders]);

  const toggleWorkflow = async (workflowId) => {
    try {
      await api.post(`/workflow-automations/${workflowId}/toggle`, {});
      fetchWorkflows();
    } catch (err) {
      console.error('Error toggling workflow:', err);
      alert('Failed to toggle workflow');
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      await api.delete(`/workflow-automations/${workflowId}`);
      fetchWorkflows();
    } catch (err) {
      console.error('Error deleting workflow:', err);
      alert('Failed to delete workflow');
    }
  };

  const updateWorkflowFolder = async (workflowId, folder) => {
    try {
      await api.patch(`/workflow-automations/${workflowId}/folder`, { folder: folder || null });
      fetchWorkflows();
      setWorkflowToMove(null);
    } catch (err) {
      console.error('Error updating folder:', err);
      alert('Failed to move workflow');
    }
  };

  const resetWorkflowStats = async (workflowId, resetType = 'failures') => {
    try {
      await api.post(`/workflow-automations/${workflowId}/reset-stats`, { reset_type: resetType });
      fetchWorkflows();
    } catch (err) {
      console.error('Error resetting stats:', err);
      alert('Failed to reset stats');
    }
  };

  const createFolder = () => {
    if (newFolderName.trim()) {
      // Folder is created implicitly when a workflow is assigned to it
      if (workflowToMove) {
        updateWorkflowFolder(workflowToMove.id, newFolderName.trim());
      }
      setNewFolderName('');
      setShowFolderModal(false);
    }
  };

  const renameFolder = async (oldName, newName) => {
    if (!newName.trim() || oldName === newName.trim()) {
      setEditingFolder(null);
      return;
    }
    
    try {
      // Update all workflows in the old folder to use the new folder name
      const workflowsInFolder = workflows.filter(wf => wf.folder === oldName);
      
      await Promise.all(workflowsInFolder.map(wf =>
        api.patch(`/workflow-automations/${wf.id}/folder`, { folder: newName.trim() })
      ));
      
      fetchWorkflows();
      setEditingFolder(null);
      setEditFolderName('');
    } catch (err) {
      console.error('Error renaming folder:', err);
      alert('Failed to rename folder');
    }
  };

  const deleteFolder = async (folderName) => {
    if (!window.confirm(`Move all workflows in "${folderName}" to Uncategorized?`)) return;
    
    try {
      const workflowsInFolder = workflows.filter(wf => wf.folder === folderName);
      
      await Promise.all(workflowsInFolder.map(wf =>
        api.patch(`/workflow-automations/${wf.id}/folder`, { folder: null })
      ));
      
      fetchWorkflows();
    } catch (err) {
      console.error('Error deleting folder:', err);
      alert('Failed to delete folder');
    }
  };

  const createEmptyFolder = () => {
    // We'll add a placeholder workflow concept or just show the folder in UI
    // For now, folders only exist when workflows are assigned to them
    // This is a UX limitation - we'll show a message
    alert('To create a folder, move a workflow into a new folder name using the "Move to folder" option.');
  };

  const handleCreateNew = () => {
    setSelectedWorkflow(null);
    setView('builder');
  };

  const handleEditWorkflow = async (workflow) => {
    try {
      setLoading(true);
      const response = await api.get(`/workflow-automations/${workflow.id}`);
      // API returns workflow data directly, not wrapped in {workflow: ...}
      setSelectedWorkflow(response.data);
      setView('builder');
    } catch (err) {
      console.error('Error fetching workflow:', err);
      alert('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogs = (workflow) => {
    setSelectedWorkflow(workflow);
    setView('logs');
  };

  const handleCloseLogs = () => {
    setView('list');
    setSelectedWorkflow(null);
  };

  const handleSaveWorkflow = async (workflowData) => {
    try {
      if (selectedWorkflow) {
        await api.put(`/workflow-automations/${selectedWorkflow.id}`, workflowData);
      } else {
        await api.post('/workflow-automations', workflowData);
      }
      setView('list');
      fetchWorkflows();
    } catch (err) {
      console.error('Error saving workflow:', err);
      alert(err.response?.data?.detail || 'Failed to save workflow');
    }
  };

  const handleCloseBuilder = () => {
    setView('list');
    setSelectedWorkflow(null);
    // Refresh workflows list in case a new workflow was created
    fetchWorkflows();
  };

  const getTriggerIcon = (triggerType) => {
    const icons = {
      'task_created': '📝',
      'task_status_changed': '🔄',
      'task_stage_changed': '📊',
      'task_assigned': '👤',
      'task_completed': '✅',
      'project_created': '📁',
      'client_created': '🏢',
      'portal_access_granted': '🔐',
      'inbound_webhook': '🔗',
      'client_portal_submission': '📨'
    };
    return icons[triggerType] || '⚡';
  };

  const getTriggerTypeLabel = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  };

  const toggleFolderExpand = (folder) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folder]: !prev[folder]
    }));
  };

  // Show workflow builder
  if (view === 'builder') {
    return (
      <WorkflowBuilder
        workflow={selectedWorkflow}
        onSave={handleSaveWorkflow}
        onClose={handleCloseBuilder}
      />
    );
  }

  // Show execution logs
  if (view === 'logs' && selectedWorkflow) {
    return (
      <WorkflowExecutionLogs
        workflow={selectedWorkflow}
        onClose={handleCloseLogs}
      />
    );
  }

  const renderWorkflowRow = (workflow) => (
    <div
      key={workflow.id}
      className="bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all p-4"
    >
      <div className="flex items-center gap-4">
        {/* Trigger Icon & Name */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="text-xl flex-shrink-0">{getTriggerIcon(workflow.trigger_type)}</span>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 truncate">{workflow.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500">
                {getTriggerTypeLabel(workflow.trigger_type)}
              </span>
              {workflow.folder && (
                <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Folder className="w-3 h-3" />
                  {workflow.folder}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="hidden sm:flex items-center gap-6 text-center flex-shrink-0">
          <div>
            <div className="text-sm font-bold text-gray-900">{workflow.stats?.execution_count || 0}</div>
            <div className="text-xs text-gray-500">Runs</div>
          </div>
          <div>
            <div className="text-sm font-bold text-green-600">{workflow.stats?.success_rate || 0}%</div>
            <div className="text-xs text-gray-500">Success</div>
          </div>
          <div>
            <div className="text-sm font-bold text-red-600">{workflow.stats?.failure_count || 0}</div>
            <div className="text-xs text-gray-500">Failures</div>
          </div>
        </div>

        {/* Last Run */}
        <div className="hidden md:flex items-center gap-2 text-xs text-gray-500 w-40 flex-shrink-0">
          {workflow.last_executed_at ? (
            <>
              <Clock className="w-3 h-3" />
              <span className="truncate">{new Date(workflow.last_executed_at).toLocaleString()}</span>
            </>
          ) : (
            <span className="text-gray-400">Never run</span>
          )}
        </div>

        {/* Toggle */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => toggleWorkflow(workflow.id)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              workflow.enabled ? 'bg-green-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                workflow.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => handleEditWorkflow(workflow)}
            className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleViewLogs(workflow)}
            className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md"
            title="View Logs"
          >
            <Activity className="w-4 h-4" />
          </button>
          <div className="relative group">
            <button
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
              title="More options"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            <div className="absolute right-0 bottom-full mb-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <button
                onClick={() => setWorkflowToMove(workflow)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
              >
                <Folder className="w-4 h-4" />
                Move to folder
              </button>
              {(workflow.stats?.failure_count > 0) && (
                <button
                  onClick={() => resetWorkflowStats(workflow.id, 'failures')}
                  className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset failure count
                </button>
              )}
              <button
                onClick={() => resetWorkflowStats(workflow.id, 'all')}
                className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset all stats
              </button>
              <button
                onClick={() => deleteWorkflow(workflow.id)}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 rounded-b-lg"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-7 h-7 text-purple-600" />
            Workflow Automations
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Create powerful automations with visual workflow builder
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg"
        >
          <Plus className="w-5 h-5" />
          Create Automation
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Filter:</span>
          <select
            value={selectedFolder === null ? '__all__' : selectedFolder}
            onChange={(e) => setSelectedFolder(e.target.value === '__all__' ? null : e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
          >
            <option value="__all__">All Folders</option>
            <option value="">Uncategorized</option>
            {folders.map(folder => (
              <option key={folder} value={folder}>{folder}</option>
            ))}
          </select>
          <button
            onClick={() => setShowFolderManager(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 text-sm"
            title="Manage folders"
          >
            <Settings className="w-4 h-4" />
            Folders
          </button>
        </div>
      </div>

      {/* Folder Manager Modal */}
      {showFolderManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Manage Folders</h3>
              <button
                onClick={() => setShowFolderManager(false)}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Folders are created when you move a workflow into a new folder. Use the pencil icon to rename or trash icon to delete folders.
            </p>
            
            {folders.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Folder className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No folders yet</p>
                <p className="text-sm text-gray-400 mt-1">Move a workflow to a folder to create one</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {folders.map(folder => (
                  <div key={folder} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Folder className="w-5 h-5 text-purple-600" />
                      {editingFolder === folder ? (
                        <input
                          type="text"
                          value={editFolderName}
                          onChange={(e) => setEditFolderName(e.target.value)}
                          onBlur={() => renameFolder(folder, editFolderName)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameFolder(folder, editFolderName);
                            if (e.key === 'Escape') setEditingFolder(null);
                          }}
                          className="px-2 py-1 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 text-sm"
                          autoFocus
                        />
                      ) : (
                        <span className="font-medium text-gray-900">{folder}</span>
                      )}
                      <span className="text-sm text-gray-500">
                        ({workflows.filter(wf => wf.folder === folder).length} workflows)
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingFolder(folder);
                          setEditFolderName(folder);
                        }}
                        className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-100 rounded"
                        title="Rename"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteFolder(folder)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Create a new folder:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Folder name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                />
                <button
                  onClick={() => {
                    if (newFolderName.trim()) {
                      // We need at least one workflow to move to create a folder
                      if (workflows.length > 0) {
                        setWorkflowToMove(workflows.find(wf => !wf.folder) || workflows[0]);
                        setShowFolderManager(false);
                      } else {
                        alert('Create a workflow first, then organize it into folders.');
                      }
                    }
                  }}
                  disabled={!newFolderName.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <FolderPlus className="w-4 h-4" />
                  Create
                </button>
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowFolderManager(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Zap className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery || selectedFolder !== null ? 'No matching workflows' : 'No automations yet'}
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {searchQuery || selectedFolder !== null 
              ? 'Try adjusting your search or filter criteria'
              : 'Create your first workflow automation to streamline your tasks.'
            }
          </p>
          {!searchQuery && selectedFolder === null && (
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg"
            >
              <Plus className="w-5 h-5" />
              Create Your First Automation
            </button>
          )}
        </div>
      ) : selectedFolder === null && !searchQuery ? (
        // Folder view when no filter/search
        <div className="space-y-6">
          {/* Folders */}
          {folders.map(folder => (
            <div key={folder} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <button
                  onClick={() => toggleFolderExpand(folder)}
                  className="flex items-center gap-3 flex-1"
                >
                  {expandedFolders[folder] ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                  <Folder className="w-5 h-5 text-purple-600" />
                  {editingFolder === folder ? (
                    <input
                      type="text"
                      value={editFolderName}
                      onChange={(e) => setEditFolderName(e.target.value)}
                      onBlur={() => renameFolder(folder, editFolderName)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameFolder(folder, editFolderName);
                        if (e.key === 'Escape') setEditingFolder(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="px-2 py-1 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 text-sm"
                      autoFocus
                    />
                  ) : (
                    <span className="font-medium text-gray-900">{folder}</span>
                  )}
                  <span className="text-sm text-gray-500">({workflowsByFolder[folder]?.length || 0})</span>
                </button>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingFolder(folder);
                      setEditFolderName(folder);
                    }}
                    className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                    title="Rename folder"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteFolder(folder);
                    }}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete folder (moves workflows to Uncategorized)"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {expandedFolders[folder] && workflowsByFolder[folder]?.length > 0 && (
                <div className="p-4 space-y-2">
                  {workflowsByFolder[folder].map(renderWorkflowRow)}
                </div>
              )}
            </div>
          ))}
          
          {/* Uncategorized */}
          {workflowsByFolder.uncategorized?.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleFolderExpand('uncategorized')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedFolders['uncategorized'] ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                  <Folder className="w-5 h-5 text-gray-400" />
                  <span className="font-medium text-gray-700">Uncategorized</span>
                  <span className="text-sm text-gray-500">({workflowsByFolder.uncategorized.length})</span>
                </div>
              </button>
              {expandedFolders['uncategorized'] && (
                <div className="p-4 space-y-2">
                  {workflowsByFolder.uncategorized.map(renderWorkflowRow)}
                </div>
              )}
            </div>
          )}
          
          {/* Show all in list if no folders exist */}
          {folders.length === 0 && (
            <div className="space-y-2">
              {filteredWorkflows.map(renderWorkflowRow)}
            </div>
          )}
        </div>
      ) : (
        // List view when filtering/searching
        <div className="space-y-2">
          {filteredWorkflows.map(renderWorkflowRow)}
        </div>
      )}

      {/* Move to Folder Modal */}
      {workflowToMove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Move to Folder</h3>
            <p className="text-sm text-gray-600 mb-4">
              Move &quot;{workflowToMove.name}&quot; to a folder:
            </p>
            
            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              <button
                onClick={() => updateWorkflowFolder(workflowToMove.id, null)}
                className={`w-full text-left px-4 py-2 rounded-lg border ${
                  !workflowToMove.folder ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className="text-gray-600">No folder (Uncategorized)</span>
              </button>
              {folders.map(folder => (
                <button
                  key={folder}
                  onClick={() => updateWorkflowFolder(workflowToMove.id, folder)}
                  className={`w-full text-left px-4 py-2 rounded-lg border flex items-center gap-2 ${
                    workflowToMove.folder === folder ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Folder className="w-4 h-4 text-purple-600" />
                  {folder}
                </button>
              ))}
            </div>
            
            <div className="border-t border-gray-200 pt-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">Or create a new folder:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="New folder name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                />
                <button
                  onClick={createFolder}
                  disabled={!newFolderName.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FolderPlus className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setWorkflowToMove(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationsSettings;
